import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { SelectInput } from "@/components/ui/Field";
import {
  cancelAllPrintQueueJobs,
  cancelPrintQueueJob,
  listPrintQueue,
  restartPrintQueueJob,
  type CupsQueueJob,
  type CupsQueuePrinter,
} from "../../lib/settingsApi";

const STATE_LABELS: Record<string, string> = {
  connecting: "Verbunden zum Drucker",
  queued: "Warteschlange",
  processing: "Druckt",
  pending: "Ausstehend",
  held: "Angehalten",
  completed: "Abgeschlossen",
  aborted: "Abgebrochen",
  canceled: "Storniert",
  unknown: "Unbekannt",
};

function formatJobState(job: CupsQueueJob): string {
  if (job.status_text) return job.status_text;
  if (job.state) {
    const label = STATE_LABELS[job.state];
    if (label) return label;
  }
  return job.state || "—";
}

function formatBytes(size?: number | null): string {
  if (size == null) return "—";
  if (size < 1024) return `${size} B`;
  return `${Math.round(size / 1024)} KB`;
}

export function PrintQueueSection({ printers }: { printers: string[] }) {
  const [queueFilter, setQueueFilter] = useState("");
  const [jobs, setJobs] = useState<CupsQueueJob[]>([]);
  const [queuePrinters, setQueuePrinters] = useState<CupsQueuePrinter[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState<string | null>(null);
  const [message, setMessage] = useState<{ variant: "success" | "error"; text: string } | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    try {
      const data = await listPrintQueue(queueFilter || undefined);
      setJobs(data.jobs ?? []);
      setQueuePrinters(data.printers ?? []);
    } catch (err) {
      setMessage({
        variant: "error",
        text: err instanceof Error ? err.message : "Warteschlange konnte nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  }, [queueFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      // Versteckter Tab: kein Hintergrund-Polling (Serverlast).
      if (document.visibilityState === "hidden") return;
      void reload({ silent: true });
    }, 10000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  async function runAction(key: string, fn: () => Promise<void>, successText: string) {
    setActionKey(key);
    setMessage(null);
    try {
      await fn();
      setMessage({ variant: "success", text: successText });
      await reloadRef.current({ silent: true });
    } catch (err) {
      setMessage({
        variant: "error",
        text: err instanceof Error ? err.message : "Aktion fehlgeschlagen.",
      });
    } finally {
      setActionKey(null);
    }
  }

  return (
    <Card
      title="Druckwarteschlangen"
      description="Aktive CUPS-Jobs auf dem Server – löschen oder neu starten."
      className="mt-4"
      actions={
        <Button type="button" size="sm" variant="secondary" disabled={loading || actionKey != null} onClick={() => void reload()}>
          Aktualisieren
        </Button>
      }
    >
      {loading && <Spinner />}
      {message && (
        <Alert variant={message.variant} title={message.variant === "success" ? "Erledigt" : "Fehler"} className="mb-4">
          {message.text}
        </Alert>
      )}

      <div className="mb-4 flex flex-wrap items-end gap-2">
        <label className="flex min-w-[220px] flex-col gap-1 text-sm">
          <span className="text-label-secondary">Drucker filtern</span>
          <SelectInput value={queueFilter} onChange={(e) => setQueueFilter(e.target.value)}>
            <option value="">Alle Drucker</option>
            {printers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </SelectInput>
        </label>
        <Button
          type="button"
          variant="danger"
          size="sm"
          disabled={loading || actionKey != null || jobs.length === 0}
          onClick={() => {
            const label = queueFilter || "alle Drucker";
            if (!window.confirm(`Alle Jobs für ${label} löschen?`)) return;
            void runAction(
              "cancel-all",
              async () => {
                await cancelAllPrintQueueJobs(queueFilter || undefined);
              },
              queueFilter ? `Warteschlange für ${queueFilter} geleert.` : "Alle Warteschlangen geleert.",
            );
          }}
        >
          Alle löschen
        </Button>
      </div>

      {!loading && queuePrinters.length > 0 && (
        <div className="mb-4 grid gap-2 md:grid-cols-2">
          {queuePrinters.map((p) => (
            <div key={p.name} className="rounded-lg border border-separator px-3 py-2 text-sm">
              <div className="font-medium text-label">{p.name}</div>
              <div className="text-label-secondary">
                Status: {p.status || p.state || "—"}
                {p.enabled === false ? " · deaktiviert" : ""}
              </div>
              {!!p.issues?.length && <div className="text-status-red">{p.issues.join(", ")}</div>}
            </div>
          ))}
        </div>
      )}

      {!loading && (
        <Table<CupsQueueJob>
          rowKey={(r) => r.job_id}
          rows={jobs}
          stickyEndColumnCount={1}
          emptyText="Keine Jobs in der Warteschlange."
          columns={[
            {
              key: "job_id",
              header: "Job",
              sortable: true,
              sortValue: (r) => r.job_id,
              render: (r) => r.job_id,
            },
            {
              key: "printer",
              header: "Drucker",
              sortable: true,
              sortValue: (r) => r.printer || "",
              render: (r) => r.printer || "—",
            },
            {
              key: "user",
              header: "Benutzer",
              sortable: true,
              sortValue: (r) => r.user || "",
              render: (r) => r.user || "—",
            },
            {
              key: "submitted_at",
              header: "Eingereiht",
              sortable: true,
              sortValue: (r) => r.submitted_at,
              render: (r) => r.submitted_at || "—",
            },
            {
              key: "size",
              header: "Größe",
              sortable: true,
              sortValue: (r) => r.size_bytes ?? -1,
              render: (r) => formatBytes(r.size_bytes),
            },
            {
              key: "state",
              header: "Status",
              sortable: true,
              sortValue: (r) => formatJobState(r),
              render: (r) => (
                <div>
                  <div>{formatJobState(r)}</div>
                  {!!r.issues?.length && <div className="text-xs text-status-red">{r.issues.join(", ")}</div>}
                </div>
              ),
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={actionKey === `restart:${r.job_id}`}
                    disabled={actionKey != null}
                    onClick={() =>
                      void runAction(
                        `restart:${r.job_id}`,
                        async () => {
                          await restartPrintQueueJob(r.job_id);
                        },
                        `Job ${r.job_id} neu gestartet.`,
                      )
                    }
                  >
                    Neu starten
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={actionKey === `cancel:${r.job_id}`}
                    disabled={actionKey != null}
                    onClick={() => {
                      if (!window.confirm(`Job ${r.job_id} löschen?`)) return;
                      void runAction(
                        `cancel:${r.job_id}`,
                        async () => {
                          await cancelPrintQueueJob(r.job_id);
                        },
                        `Job ${r.job_id} gelöscht.`,
                      );
                    }}
                  >
                    Löschen
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}
